<!-- @format -->
<template>
    <div class="add-activity-to-opportunity-widget">
        <div class="widget-header">
            <div class="title-container">
                <h3>Add Activity to an Opportunity</h3>
            </div>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>

        <div class="content-wrapper">
            <div v-if="step === 'updateActivity'" class="activity-step">
                <div class="activity-form-row">
                    <div v-if="activity.contact" class="input-wrapper contact">
                        <label>Contact</label>
                        <div class="read-only-contact">
                            <div v-show="activity.contact.imageUrl" class="profile-img" :style="{ backgroundImage: 'url(\'' + activity.contact.imageUrl + '\')' }"></div>
                            <div class="name">{{ activity.contact.name }}</div>
                        </div>
                    </div>
                    <div class="input-wrapper type">
                        <label>Activity Type</label>
                        <p>{{ activity.type }}</p>
                    </div>
                    <div class="input-wrapper contact">
                        <label>What opportunity is this activity related to?</label>
                        <opportunity-search
                            v-model="activity.opportunity"
                            :client-id="clientId"
                            :company-id="options.companyId"
                        />
                    </div>
                </div>
            </div>
            <div
                v-else-if="step === 'saving' || step === 'loading'"
                class="activity-step saving-step"
            >
                <quick-add-saving-svg />
            </div>
        </div>

        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
            </div>
            <div class="action-container">
                <app-button
                    :loading="steps[step].loading"
                    @click="steps[step].action"
                >
                    {{ steps[step].buttonText }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const OpportunitySearch = () => import('@/components/opportunity/opportunity-search');
const QuickAddSavingSvg = () => import('@/components/svg/quick-add/quick-add-saving-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'add-activity-to-opportunity-widget',

    components: {
        'app-button': AppButton,
        'opportunity-search': OpportunitySearch,
        'quick-add-saving-svg': QuickAddSavingSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },

        options: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            clientId: this.options.clientId,

            activity: {
                id: this.options.activityId,
                type: this.options.type,
                contactId: this.options.contactId,
                contact: null,
                opportunity: null,
            },

            step: 'updateActivity',
            steps: {
                loading: {
                    buttonText: 'Loading',
                    action: () => {},
                    loading: true,
                },

                updateActivity: {
                    buttonText: 'Save',
                    action: this.addActivityToOpportunity,
                    loading: false,
                },

                saving: {
                    buttonText: 'Saving',
                    action: () => {},
                    loading: true,
                },
            },
        };
    },

    created() {
        this.getContact();
    },

    methods: {
        getContact() {
            this.step = 'loading';
            
            var filter = `$filter=Id eq ${ this.activity.contactId }`;
            var select = '$select=Id,Email,FirstName,LastName,ProfileImageUrl';

            var queryParameters = `${ filter }&${ select }&$count=true`;
            var restUrl = `/service/Contacts?${ queryParameters }`;

            this.$api
                .get(restUrl)
                .then(response => {
                    var contact = { 
                        id: response.data.value[0].Id,
                        email: response.data.value[0].Email,
                        name: this.$helpers.getName(response.data.value[0].FirstName, response.data.value[0].LastName),
                        imageUrl: response.data.value[0].ProfileImageUrl || require('@/assets/images/avatar.png')
                    };

                    this.activity.contact = contact;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while fetching your contacts.', { error });
                })
                .finally(() => {
                    this.step = 'updateActivity';
                });
        },

        addActivityToOpportunity() {
            this.isLoading = true;

            let restUrl = '/api/Activities/Deal';
            
            this.step = 'saving';
            if (this.activity.type === 'Meeting') {
                restUrl = `/api/${this.activity.type}Activities/${this.activity.id}`;
                var changes = [
                    {
                        name: 'DealId',
                        value: this.activity.opportunity.id,
                    },
                ];

                this.$api
                    .patch(restUrl, changes)
                    .then(() => {
                        EventBus.$emit('activity:update');
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while saving your activity.',
                            { error },
                        );
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });

            } else {
                const bodyData = {
                    ActivitiesId: [this.activity.id],
                    DealId: this.activity.opportunity.id,
                }
    
                this.$api
                    .odataPatch(restUrl, bodyData)
                    .then(() => {
                        EventBus.$emit('activity:update');
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while saving your activity.',
                            { error },
                        );
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }

        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.add-activity-to-opportunity-widget .loading-screen {
    margin: 0 auto;
}

.add-activity-to-opportunity-widget .activity-step {
    width: 100%;
}
.add-activity-to-opportunity-widget .activity-step .activity-form-row {
    display: flex;
    flex-direction: column;
}

.add-activity-to-opportunity-widget .activity-step .activity-form-row .input-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 12px;
}

.add-activity-to-opportunity-widget .activity-step.scrub-step .activity-form-row {
    flex-direction: column;
}

.add-activity-to-opportunity-widget .activity-step.saving-step svg {
    width: 100%;
}

.add-activity-to-opportunity-widget .action-container {
    display: flex;
}

.add-activity-to-opportunity-widget .action-container .btn {
    margin-left: 15px;
}

.add-activity-to-opportunity-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.add-activity-to-opportunity-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.add-activity-to-opportunity-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}

.add-activity-to-opportunity-widget .activity-step .activity-form-row .label {
    display: block;
    font-size: 16px;
    font-family: 'Roboto';
    font-weight: 400;
    margin-bottom: 7px;
}

.add-activity-to-opportunity-widget .read-only-contact {
    display: flex;
    align-items: center;
}

.add-activity-to-opportunity-widget .read-only-contact .profile-img {
    height: 32px;
    width: 32px;
    margin-right: 18px;
    border-radius: 50%;
    background-size: cover;
    background-position: top left;
    background-repeat: no-repeat;
}
</style>
